import EntityListSelect from '@/components/UI/EntityListSelect';
import FormSelect from '@/components/UI/FormSelect';
import FormSwitch from '@/components/UI/FormSwitch';
import { Card } from '@/components/UI';
import { parseTradingStrategyDurationInput, type TradingStrategyFormData } from '../tradingStrategyEditorSubmitPayloads';
import { durationOptions, entityTypeOptions, futuresDurationOptions } from './constants';

interface TradingConfigurationCardProps {
    formData: TradingStrategyFormData;
    onInputChange: <Field extends keyof TradingStrategyFormData>(field: Field, value: TradingStrategyFormData[Field]) => void;
    validationErrors: Record<string, string>;
}

const TradingConfigurationCard = ({ formData, onInputChange, validationErrors }: TradingConfigurationCardProps) => (
    <Card subtitle="Which entities this strategy evaluates, and on what bar." title="Universe and timing">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormSelect
                label="Primary duration"
                options={formData.allowedUnderlyingEntityTypes.includes('FuturesProduct') ? futuresDurationOptions : durationOptions}
                value={formData.duration}
                onChange={(e) => onInputChange('duration', parseTradingStrategyDurationInput(e.target.value, formData.duration))}
            />
            <EntityListSelect
                helpText="Scope backtests to a saved entity list. Leave unset to use all monitored entities."
                label="Entity list scope"
                placeholder="All monitored entities"
                value={formData.entityListId ?? null}
                onChange={(value) => onInputChange('entityListId', value)}
            />
        </div>
        <div className="mt-4">
            <div className="mb-2 block text-sm font-medium text-zinc-300">Allowed entity types</div>
            <div className="flex gap-4">
                {entityTypeOptions.map((option) => (
                    <label className="flex items-center text-white" key={option.value}>
                        <input
                            checked={formData.allowedUnderlyingEntityTypes.includes(option.value)}
                            className="mr-2"
                            type="checkbox"
                            onChange={(e) => {
                                if (e.target.checked) {
                                    onInputChange(
                                        'allowedUnderlyingEntityTypes',
                                        option.value === 'FuturesProduct'
                                            ? [option.value]
                                            : [
                                                  ...formData.allowedUnderlyingEntityTypes.filter((type) => type !== 'FuturesProduct'),
                                                  option.value
                                              ]
                                    );
                                } else {
                                    onInputChange(
                                        'allowedUnderlyingEntityTypes',
                                        formData.allowedUnderlyingEntityTypes.filter((t) => t !== option.value)
                                    );
                                }
                            }}
                        />
                        {option.label}
                    </label>
                ))}
            </div>
            {validationErrors.allowedUnderlyingEntityTypes && (
                <div className="mt-1 text-sm text-red-400">{validationErrors.allowedUnderlyingEntityTypes}</div>
            )}
            {formData.allowedUnderlyingEntityTypes.includes('FuturesProduct') && (
                <p className="mt-1 text-xs text-zinc-400">
                    Futures research uses one outright Future leg, 1m or 5m bars, exchange sessions and an inactive strategy. Only 5m
                    strategies can size by risk to a captured stop. Live trading and scheduled strategy backtests remain disabled.
                </p>
            )}
            {formData.allowedUnderlyingEntityTypes.includes('AssetIndex') && (
                <p className="mt-1 text-xs text-zinc-400">
                    Index options use the standard backtest workflow. Close positions before their last trading time; expiration settlement
                    is not supported. Live activation requires a verified index-only list and regular-session bars.
                </p>
            )}
        </div>
        <div className="mt-4">
            <FormSwitch
                checked={formData.afterHoursRemoved}
                disabled={formData.allowedUnderlyingEntityTypes.includes('FuturesProduct')}
                label="Remove after-hours data"
                onChange={(e) => onInputChange('afterHoursRemoved', e.target.checked)}
            />
        </div>
    </Card>
);

export default TradingConfigurationCard;
